/**
 * PTTButton Component
 * 
 * Large push-to-talk button for mobile and desktop.
 * - Hold to talk, release to send
 * - Visual states: idle, transmitting, waiting, AI responding
 * - Touch events (mobile) + mouse events (desktop)
 * - Haptic feedback on press/release (mobile)
 */

import { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MicrophoneIcon } from '@heroicons/react/24/solid';

// Detect if we're on desktop (not touch device)
function isDesktop(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

interface PTTButtonProps {
  onPTTPressed?: () => void;
  onPTTReleased?: () => void;
  isTransmitting?: boolean;
  isWaiting?: boolean;
  isAIResponding?: boolean;
  disabled?: boolean;
  /** When true, touch events are handled externamente (e.g. by a draggable wrapper). PTTButton only renders visuals. */
  externalTouchControl?: boolean;
  hasUsedPTT?: boolean;
}

export default function PTTButton({
  onPTTPressed,
  onPTTReleased,
  isTransmitting = false,
  isWaiting = false,
  isAIResponding = false,
  disabled = false,
  externalTouchControl = false,
  hasUsedPTT = false,
}: PTTButtonProps) {
  const { t } = useTranslation();
  const isPressedRef = useRef(false);

  // 🎯 Anti-flash: When transitioning from "waiting" (processing) to "AI responding",
  // there can be 1-2 frames where neither isWaiting nor isAIResponding is true,
  // causing a brief green (idle) flash. We "stick" the waiting state until AI responding kicks in.
  const [stickyWaiting, setStickyWaiting] = useState(false);

  useEffect(() => {
    if (isWaiting) {
      // Entered waiting state — activate sticky
      setStickyWaiting(true);
    } else if (stickyWaiting) {
      // isWaiting went false — only clear sticky when a definitive next state arrives
      if (isAIResponding || isTransmitting) {
        setStickyWaiting(false);
      }
      // Otherwise keep stickyWaiting true to prevent the idle flash
    }
  }, [isWaiting, isAIResponding, isTransmitting, stickyWaiting]);

  // Effective waiting: real waiting OR sticky waiting (bridging the gap)
  const effectiveWaiting = isWaiting || (stickyWaiting && !isAIResponding && !isTransmitting);

  const triggerHaptic = useCallback(() => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(15);
    }
  }, []);

  const handlePressStart = useCallback(() => {
    if (disabled || isPressedRef.current) return;
    isPressedRef.current = true;
    triggerHaptic();
    onPTTPressed?.();
  }, [disabled, onPTTPressed, triggerHaptic]);

  const handlePressEnd = useCallback(() => {
    if (!isPressedRef.current) return;
    isPressedRef.current = false;
    triggerHaptic();
    onPTTReleased?.();
  }, [onPTTReleased, triggerHaptic]);

  // Determine visual state (using effectiveWaiting to prevent idle flash)
  const isIdle = !isTransmitting && !effectiveWaiting && !isAIResponding;

  // Detect if we're on desktop
  const desktop = isDesktop();
  
  // Hide label on desktop after user has used PTT at least once
  const hideLabel = desktop && hasUsedPTT;

  let label = desktop 
    ? t('scenario.session.ptt.holdSpaceToTalk', 'Press Space or click to talk')
    : t('scenario.session.ptt.holdToTalk', 'Hold to talk');
  if (isTransmitting) label = t('scenario.session.ptt.listening', 'Listening...');
  if (effectiveWaiting) label = t('scenario.session.ptt.processing', 'Processing...');
  if (isAIResponding) label = t('scenario.session.ptt.aiSpeaking', 'AI speaking...');

  return (
    // Fixed width = button width (7rem mobile / 6rem desktop) and absolutely-positioned
    // label so text changes ("Hold to talk" → "Listening..." → "Processing...") don't
    // resize the container and shift the visual X position when centered with translateX(-50%).
    <div className="relative flex flex-col items-center w-[7rem] md:w-24">
      {/*
        Button with touch/mouse events directly on the button element.
        No extra padding - the touch target is exactly the button size.
      */}
      <button
        type="button"
        {...(!externalTouchControl ? {
          onTouchStart: (e: React.TouchEvent) => { e.preventDefault(); handlePressStart(); },
          onTouchEnd: (e: React.TouchEvent) => { e.preventDefault(); handlePressEnd(); },
          onTouchCancel: (e: React.TouchEvent) => { e.preventDefault(); handlePressEnd(); },
        } : {})}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={() => { if (isPressedRef.current) handlePressEnd(); }}
        onContextMenu={(e) => e.preventDefault()}
        className={`
          w-[7rem] h-[7rem] md:w-24 md:h-24 rounded-full
          flex items-center justify-center
          transition-all duration-150
          select-none touch-none pointer-events-auto
          ${disabled
            ? 'bg-gray-400 cursor-not-allowed'
            : isTransmitting
              ? 'bg-red-500 scale-110 shadow-red-500/50 shadow-2xl ring-4 ring-red-400/50 animate-pulse cursor-pointer'
              : effectiveWaiting
                ? 'bg-yellow-500 shadow-yellow-500/30 cursor-wait'
                : isAIResponding
                  ? 'bg-blue-500/60 shadow-blue-500/20 cursor-wait'
                  : 'bg-green-600 shadow-lg cursor-pointer hover:bg-green-500 hover:scale-105 hover:shadow-green-500/40 hover:shadow-xl'
          }
        `}
        role="button"
        aria-label={label}
        aria-disabled={disabled}
        disabled={disabled}
      >
        {effectiveWaiting ? (
          <svg className="animate-spin h-10 w-10 md:h-10 md:w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          <MicrophoneIcon className={`w-10 h-10 md:w-10 md:h-10 text-white ${isTransmitting ? 'animate-bounce' : ''}`} />
        )}
      </button>
      
      {/* Label below button — absolutely positioned so it doesn't affect container width
          and cannot shift the button's horizontal position when the text changes. */}
      {!hideLabel && (
        <span className={`absolute top-full mt-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-sm md:text-sm font-medium transition-opacity duration-300 ${
          isTransmitting ? 'text-red-300' : effectiveWaiting ? 'text-yellow-300' : isIdle ? 'text-white/70' : 'text-blue-300'
        }`}>
          {label}
        </span>
      )}
    </div>
  );
}
